import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../utils/AxiosInstance";
import { useAuth } from "../context/AuthContext";
import SearchResultsTable from "./SearchResultsTable";
import SearchContainer from "../components/SearchContainer";

// === Validation and Detection Utilities ===
const validateHash = (val) =>
  /^[a-fA-F0-9]{32}$|^[a-fA-F0-9]{40}$|^[a-fA-F0-9]{64}$/.test(val);
const validateIP = (val) => /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(val);
const validateEmail = (val) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val);
const validateDomain = (val) =>
  /^[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,}$/i.test(val);
const validateURL = (val) => {
  const urlRegex = /^(https?:\/\/)([a-zA-Z0-9.-]+|\d{1,3}(?:\.\d{1,3}){3})(:\d+)?(\/[^\s]*)?$/i;
  return urlRegex.test(val);
};

const detectIndicatorType = (val) => {
  if (validateEmail(val)) return "email";
  if (validateIP(val)) return "ip";
  if (validateDomain(val)) return "domain";
  if (validateURL(val)) return "url";
  if (validateHash(val)) return "hash";
  return null;
};

const Search = () => {
  const [query, setQuery] = useState("");
  const [indicatorType, setIndicatorType] = useState(null);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [searchCount, setSearchCount] = useState(null);
  const [loadingSearchCount, setLoadingSearchCount] = useState(false);
  const [searchCountError, setSearchCountError] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [overallScore, setOverallScore] = useState(null);
  const [historyCollapsed, setHistoryCollapsed] = useState(true);
  const [historyLimit, setHistoryLimit] = useState(5);
  const [dateRange, setDateRange] = useState("all");
  const { accessToken } = useAuth();

  const fetchHistory = useCallback(async () => {
    if (!accessToken) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await axiosInstance.get("search/history/", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setHistory(response.data);
    } catch (err) {
      console.error("Failed to fetch search history", err);
      setHistoryError("Failed to load search history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axiosInstance.get("share/stats/");
        setStats(response.data);
        setStatsError(false);
      } catch (err) {
        console.error("Failed to fetch stats", err);
        setStats(null);
        setStatsError(true);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const getSearchCount = async (indicator) => {
    try {
      const response = await axiosInstance.get("search/count/", {
        params: { indicator },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data.search_count;
    } catch (error) {
      console.error("Failed to fetch search count:", error);
      throw error;
    }
  };

  const appendHttpsIfMissing = (url) => {
    if (typeof url === "string" && !url.match(/^https?:\/\//i)) {
      return "https://" + url;
    }
    return url;
  };

const formatDate = (date) => date.toISOString().slice(0, 10);

const handleSearch = async (e) => {
  e.preventDefault();

  if (!query) {
    setError("Please enter a search query.");
    return;
  }

  let modifiedQuery = query.trim();
  let detectedType = detectIndicatorType(modifiedQuery);

  if (detectedType === "url" && !/^https?:\/\//i.test(modifiedQuery)) {
    modifiedQuery = appendHttpsIfMissing(modifiedQuery);
    detectedType = detectIndicatorType(modifiedQuery);
  }

  if (!detectedType) {
    setError("Unable to detect the indicator type or invalid format.");
    return;
  }

  // Calculate start_date and end_date based on dateRange
  let startDate = null;
  let endDate = null;
  const today = new Date();

  if (dateRange && dateRange !== "all") {
    endDate = formatDate(today);
    const pastDate = new Date(today);
    pastDate.setDate(pastDate.getDate() - Number(dateRange));
    startDate = formatDate(pastDate);
  }

  setIndicatorType(detectedType);
  setError(null);
  setLoading(true);

  try {
    const response = await axiosInstance.post(
      "search/search/",
      {
        indicator: modifiedQuery,
        indicator_type: detectedType,
        start_date: startDate,
        end_date: endDate,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    setResults(Object.values(response.data.results));
    setOverallScore(response.data.overall_score);

    setLoadingSearchCount(true);
    setSearchCountError(null);
    try {
      const count = await getSearchCount(modifiedQuery);
      setSearchCount(count);
    } catch (err) {
      setSearchCountError("Error fetching search count.");
    }
    setLoadingSearchCount(false);

    await fetchHistory();
  } catch (err) {
    console.error(err);
    if (err.response?.data?.detail) {
      setError(err.response.data.detail);
    } else {
      setError("An error occurred while fetching the results.");
    }
  } finally {
    setLoading(false);
  }
};


  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setIndicatorType(detectIndicatorType(value));
  };

return (
  <SearchContainer>
    <div className="bg-gray-900 text-white py-16 px-4 min-h-screen">
      {/* Updated: Replace outer flex justify-center with container to match max width and centering */}
      <div className="max-w-6xl mx-auto">
        <form
          onSubmit={handleSearch}
          className="flex items-center w-full gap-4"
        >
          <div className="relative flex-grow min-w-0">
            <input
              type="text"
              placeholder="Search IP, domain, URL, or hash"
              value={query}
              onChange={handleChange}
              className="w-full pl-12 pr-4 py-3 rounded-md bg-[#1f1f2e] border border-[#3c3c52] placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#9b84e2]"
              required
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z"
                />
              </svg>
            </div>
          </div>

          {/* Date Range Selector */}
          <div className="max-w-xs flex-shrink-0 relative">
          <select
            id="dateRange"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-[#1f1f2e] border border-[#3c3c52] rounded px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#9b84e2] w-full"
          >
            <option value="all">All time</option>
            <option value="1">Last 1 day</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 1 year</option>
          </select>
          <p className="absolute left-0 top-full mt-1 text-xs text-gray-400">
            Time selector applies to Yivo database only
          </p>
        </div>

          <button
            type="submit"
            className="px-5 py-3 bg-[#6b5ca5] hover:bg-[#8473c9] transition-colors text-white font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-[#9b84e2]"
          >
            Search
          </button>
        </form>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center mt-10">
          <div className="w-6 h-6 border-4 border-[#9b84e2] border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {/* Type Detection */}
      {indicatorType && (
        <div className="text-sm text-purple-300 text-center pt-10">
          Detected Indicator Type:{" "}
          <span className="font-medium text-white">{indicatorType}</span>
        </div>
      )}
      {/* ----------- Collapsible Search History ----------- */}
      <div className="max-w-6xl mx-auto mt-12 bg-[#2b2b40] rounded-xl border border-[#3f3f61] p-4">
        <button
          className="flex items-center justify-between w-full text-white font-semibold text-lg focus:outline-none"
          onClick={() => setHistoryCollapsed(!historyCollapsed)}
          aria-expanded={!historyCollapsed}
          aria-controls="search-history-content"
        >
          <span>Search History</span>
          <svg
            className={`w-5 h-5 transition-transform duration-300 ${
              historyCollapsed ? "rotate-0" : "rotate-180"
            }`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {!historyCollapsed && (
          <div id="search-history-content" className="mt-4">
            {/* Limit selector */}
            <div className="mb-3 flex items-center gap-3 text-sm text-gray-400">
              Show last:
              {[5, 10, 25].map((num) => (
                <button
                  key={num}
                  className={`px-3 py-1 rounded ${
                    historyLimit === num
                      ? "bg-[#6b5ca5] text-white"
                      : "hover:bg-[#45456f] text-gray-400"
                  } transition-colors`}
                  onClick={() => setHistoryLimit(num)}
                  type="button"
                >
                  {num}
                </button>
              ))}
            </div>

            {historyLoading && (
              <div className="flex justify-center">
                <div className="w-6 h-6 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {historyError && (
              <div className="text-red-500 text-center">{historyError}</div>
            )}

            {!historyLoading && !historyError && history.length === 0 && (
              <p className="text-gray-400 text-center">No search history found.</p>
            )}

            {!historyLoading && !historyError && history.length > 0 && (
              <ul className="space-y-2 max-h-64 overflow-y-auto">
                {history
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .slice(0, historyLimit)
                  .map((entry) => (
                    <li
                      key={entry.id}
                      className="p-2 bg-[#1f1f2e] rounded-md border border-[#3c3c52] flex justify-between items-center"
                    >
                      <div>
                        <span className="font-semibold text-white mr-2">
                          {entry.indicator}
                        </span>
                        <span className="capitalize text-sm text-purple-300 mr-4">
                          ({entry.indicator_type})
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(entry.created_at).toLocaleString()}
                        </span>
                      </div>
                      <button
                        className="ml-4 text-sm bg-[#6b5ca5] hover:bg-[#8473c9] text-white px-3 py-1 rounded"
                        onClick={() => {
                          setQuery(entry.indicator);
                          setIndicatorType(entry.indicator_type);
                          setTimeout(() => {
                            document.querySelector("form").dispatchEvent(
                              new Event("submit", { bubbles: true, cancelable: true })
                            );
                          }, 0);
                        }}
                      >
                        Search Again
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Error */}
      {error && <div className="text-red-400 text-center mt-6">{error}</div>}


      {/* Stats */}
      {stats === null && statsError ? (
        <div className="flex flex-col justify-center items-center mt-12 text-red-400">
          <svg
            className="h-10 w-10 mb-2"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-semibold">
            Failed to load statistics. Please try again later.
          </span>
        </div>
      ) : stats === null ? (
        // Loading state
        <div className="flex justify-center items-center mt-12">
          <svg
            className="animate-spin h-10 w-10 text-indigo-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          <span className="ml-3 text-indigo-400 font-semibold">Loading stats...</span>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto mt-12 space-y-10">
          {/* Main Stats */}
          <div className="flex flex-col sm:flex-row bg-[#2b2b40] rounded-2xl border border-[#2f2f46] overflow-hidden">
            {/* Total Indicators */}
            <div className="flex-1 p-8 flex flex-col items-center bg-indigo-900/10">
              <p className="text-6xl font-extrabold text-indigo-400 mb-2">
                {Object.values(stats.by_type).reduce((acc, val) => acc + val, 0).toLocaleString()}
              </p>
              <h4 className="text-sm uppercase text-indigo-200 tracking-widest">
                Total Indicators
              </h4>
            </div>

            {/* Vertical Divider */}
            <div className="hidden sm:block w-px bg-[#3f3f61]" />

            {/* Distinct Feed Sources */}
            <div className="flex-1 p-8 flex flex-col items-center bg-green-900/10">
              <p className="text-6xl font-extrabold text-green-400 mb-2">
                {Object.keys(stats.by_source).length}
              </p>
              <h4 className="text-sm uppercase text-green-200 tracking-widest">
                Distinct Feed Sources
              </h4>
            </div>
          </div>

          <div className="bg-[#2b2b40] rounded-2xl p-6 border border-[#2f2f46] max-w-full overflow-x-auto">
            <h4 className="text-center text-sm uppercase text-gray-400 tracking-wide mb-6">
              IOCs by Type
            </h4>
            <div
              className="grid grid-cols-[repeat(auto-fit,minmax(0,1fr))] rounded-lg border-l border-r border-[#3f3f61] divide-x divide-[#3f3f61]"
              style={{
                gridTemplateColumns: `repeat(${Object.keys(stats.by_type).length}, minmax(0, 1fr))`,
                minWidth: "600px",
              }}
            >
              {Object.entries(stats.by_type).map(([type, count], index) => {
                const lineColors = [
                  "bg-indigo-400",
                  "bg-green-400",
                  "bg-pink-400",
                  "bg-yellow-400",
                ];
                const lineColor = lineColors[index % lineColors.length];
                return (
                  <div
                    key={type}
                    className={`flex flex-col items-center p-5 text-center ${
                      index === 0 ? "rounded-l-lg" : ""
                    } ${
                      index === Object.entries(stats.by_type).length - 1 ? "rounded-r-lg" : ""
                    }`}
                  >
                    <div className={`h-1 w-12 mb-3 rounded ${lineColor}`}></div>
                    <span className="text-3xl font-extrabold text-white">
                      {count.toLocaleString()}
                    </span>
                    <span className="mt-2 capitalize text-gray-300 font-semibold tracking-wide text-sm">
                      {type}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="mt-4 max-w-6xl mx-auto">
          <SearchResultsTable
            results={results}
            indicator={query}
            searchCount={searchCount}
            loadingSearchCount={loadingSearchCount}
            searchCountError={searchCountError}
            overallScore={overallScore}
          />
        </div>
      )}
    </div>
  </SearchContainer>
);
};

export default Search;

